import { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { t } from 'i18next'
import SectionForm from '../../common/sectionForm'
import EditButtons from '../../common/editButtons'
import { EditableContext } from '../../../context/editableContext'
import FormikContext from '../../../context/formikContext'
import ReadOnlyField from '../../common/fields/readOnlyField'
import EditableTextField from '../../common/fields/editableTextField'
import OutlinedFieldFormik from '../../formik/fields/outlinedFieldFormik'
import SelectMultipleFormik from '../../formik/fields/selectMultipleFormik'
import SectionFormAccordion from '../../common/sectionFormAccordion'
import { Typography, useMediaQuery } from '@mui/material'
import { getDayAndMonthName, getDayAndMonthNameYear, getNestedObject, getYear } from '../../../utils'
import PhoneFieldFromik from '../../formik/fields/phoneFieldFromik'
import EmailFieldFormik from '../../formik/fields/emailFieldFormik'
import AmountFieldFromik from '../../formik/fields/amountFieldFormik'
import YearFieldFormik from '../../formik/fields/yearFieldFormik'
import { FormikProps } from 'formik'
import useIsGrantedToUpdate from '../../../hooks/useIsGrantedToUpdate'
interface Props {
  formik: FormikProps<any>
  isEditable: boolean
  setIsEditable: React.Dispatch<React.SetStateAction<boolean>>
}
const listItems = [
  {
    name: 'Commercial',
    id: 'Commercial',
  },
  {
    name: 'Industriel',
    id: 'Industriel',
  },
  {
    name: 'Institutionnel',
    id: 'Institutionnel',
  },
  {
    name: 'Génie civil',
    id: 'Genie_civil',
  },
  {
    id: 'Voirie',
    name: 'Voirie',
  },
  {
    name: 'Résidentiel - Rénovation',
    id: 'Residentiel_renovation',
  },
  {
    name: 'Résidentiel - Constructeur',
    id: 'Residentiel_constructeur', 
  },
]

const translate = (item: string) => t(`pages.entrepriseProfile.general.${item}`)!
const GeneralForm = ({ isEditable, setIsEditable, formik }: Props) => {
  const isMobile = useMediaQuery('(max-width:800px)')
  const isSmallMobile = useMediaQuery('(max-width:480px)')
  const getValue = (property: string) => {
    return (getNestedObject(property, formik.values) || '') as string
  }
  const editableState = useMemo(() => ({ isEditable, setIsEditable }), [isEditable, setIsEditable])
  const isGrantedToUpdate = useIsGrantedToUpdate()
  return (
    <EditableContext.Provider value={editableState}>
      <FormikContext.Provider value={formik}>
        <Box component='form' sx={{ pt: isSmallMobile ? '24px' : isMobile ? '36px' : '48px', position: 'relative', overflow: 'hidden' }}>
          {isGrantedToUpdate && (
            <EditButtons sx={{ position: 'absolute', top: isSmallMobile ? '12px' : isMobile ? '24px' : '48px', right: '0' }} />
          )}
          <Stack spacing={isSmallMobile ? '20px' : isMobile ? '24px' : '30px'} mt={isGrantedToUpdate ? (isSmallMobile ? '40px' : isMobile ? '52px' : '66px') : '0'} mb={isSmallMobile ? '20px' : '30px'}>
            <SectionForm title={translate('aboutEntreprise.title')}>
              <Stack spacing={isSmallMobile ? '16px' : isMobile ? '20px' : '27px'}>
                {/* Row 1 - Responsive layout */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '0'}>
                  <ReadOnlyField
                    label={translate('aboutEntreprise.legalName')}
                    value={getValue('about.legal_name')}
                    sx={{ 
                      width: isMobile ? '100%' : '46%', 
                      mr: isMobile ? '0' : '6%',
                      mb: isMobile ? '16px' : '0'
                    }}
                  />
                  {!isMobile && (
                    <>
                      <ReadOnlyField
                        label={translate('aboutEntreprise.RGB')}
                        value={getValue('about.no_rbq')}
                        sx={{ width: '22%', mr: '2%' }}
                      />
                      <ReadOnlyField
                        label={translate('aboutEntreprise.RBGIssueDate')}
                        value={getValue('about.rbq_issue_date') || 'Non disponible'}
                        sx={{ width: '22%' }}
                      />
                    </>
                  )}
                </Stack>
                
                {/* Mobile: Additional fields from row 1 */}
                {isMobile && (
                  <Stack direction={isSmallMobile ? 'column' : 'row'} spacing={isSmallMobile ? '16px' : '16px'}>
                    <ReadOnlyField
                      label={translate('aboutEntreprise.RGB')}
                      value={getValue('about.no_rbq')}
                      sx={{ width: isSmallMobile ? '100%' : '48%' }}
                    />
                    <ReadOnlyField
                      label={translate('aboutEntreprise.RBGIssueDate')}
                      value={getValue('about.rbq_issue_date') || 'Non disponible'}
                      sx={{ width: isSmallMobile ? '100%' : '48%' }}
                    />
                  </Stack>
                )}

                {/* Row 2 - Responsive layout */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '0'}>
                  <EditableTextField
                    label={translate('aboutEntreprise.usualName')}
                    sx={{ 
                      width: isMobile ? '100%' : '46%', 
                      mr: isMobile ? '0' : '6%',
                      mb: isMobile ? '16px' : '0'
                    }}
                    value={getValue('about.usual_name')}
                  >
                    <OutlinedFieldFormik
                      name='about.usual_name'
                      placeholder={translate('aboutEntreprise.usualName')}
                      sx={{ maxWidth: isMobile ? '100%' : '469px' }}
                    />
                  </EditableTextField>
                  {!isMobile && (
                    <>
                      <ReadOnlyField
                        label={translate('aboutEntreprise.RBQRenewal')}
                        value={getDayAndMonthName(getValue('about.rbq_renovation_date'))}
                        sx={{ width: '22%', mr: '2%' }}
                      />
                      <ReadOnlyField
                        label={translate('aboutEntreprise.CCQ')}
                        value={getValue('about.no_ccq')}
                        sx={{ width: '22%' }}
                      />
                    </>
                  )}
                </Stack>
                
                {/* Mobile: Additional fields from row 2 */}
                {isMobile && (
                  <Stack direction={isSmallMobile ? 'column' : 'row'} spacing={isSmallMobile ? '16px' : '16px'}>
                    <ReadOnlyField
                      label={translate('aboutEntreprise.RBQRenewal')}
                      value={getDayAndMonthName(getValue('about.rbq_renovation_date'))}
                      sx={{ width: isSmallMobile ? '100%' : '48%' }}
                    />
                    <ReadOnlyField
                      label={translate('aboutEntreprise.CCQ')}
                      value={getValue('about.no_ccq')}
                      sx={{ width: isSmallMobile ? '100%' : '48%' }}
                    />
                  </Stack>
                )}

                {/* Row 3 - Responsive layout */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '0'}>
                  <EditableTextField
                    label={translate('aboutEntreprise.activityArea')}
                    sx={{ 
                      width: isMobile ? '100%' : '46%', 
                      mr: isMobile ? '0' : '6%',
                      mb: isMobile ? '16px' : '0'
                    }}
                    value={listItems
                      .filter((option) => getValue('about.business_sectors').includes(option.id))
                      .map((option) => option.name)
                      .join(', ')}
                  >
                    <SelectMultipleFormik
                      name='about.business_sectors'
                      placeholder={translate('aboutEntreprise.activityArea')}
                      listItems={listItems}
                      selectType={true}
                      sx={{ maxWidth: isMobile ? '100%' : '469px' }}
                    />
                  </EditableTextField>
                  {!isMobile && (
                    <>
                      <ReadOnlyField
                        label={translate('aboutEntreprise.BSDQ')}
                        value={getValue('about.no_bsdq')}
                        sx={{ width: '22%', mr: '2%' }}
                      />
                      <ReadOnlyField
                        label={translate('aboutEntreprise.NEQ')}
                        value={getValue('about.no_neq')}
                        sx={{ width: '22%' }}
                      />
                    </>
                  )}
                </Stack>
                
                {/* Mobile: Additional fields from row 3 */}
                {isMobile && (
                  <Stack direction={isSmallMobile ? 'column' : 'row'} spacing={isSmallMobile ? '16px' : '16px'}>
                    <ReadOnlyField
                      label={translate('aboutEntreprise.BSDQ')}
                      value={getValue('about.no_bsdq')}
                      sx={{ width: isSmallMobile ? '100%' : '48%' }}
                    />
                    <ReadOnlyField
                      label={translate('aboutEntreprise.NEQ')}
                      value={getValue('about.no_neq')}
                      sx={{ width: isSmallMobile ? '100%' : '48%' }}
                    />
                  </Stack>
                )}

                {/* Row 4 - Company details */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '0'} sx={{ width: isMobile ? '100%' : '46%', maxWidth: isMobile ? '100%' : '469px' }}>
                  <EditableTextField
                    label={translate('aboutEntreprise.foundingDate')}
                    sx={{ 
                      width: isMobile ? '100%' : '100%', 
                      mr: isMobile ? '0' : '5%',
                      mb: isMobile ? '16px' : '0'
                    }}
                    value={getYear(getValue('about.founding_date')) || '_'}
                  >
                    <YearFieldFormik
                      name='about.founding_date'
                      placeholder={translate('aboutEntreprise.foundingDate')}
                      sx={{ maxWidth: isMobile ? '100%' : '222px' }}
                    />
                  </EditableTextField>
                  <EditableTextField
                    label={translate('aboutEntreprise.numberOfEmployees')}
                    sx={{ width: '100%' }}
                    value={getValue('about.number_of_employees') || '_'}
                  >
                    <AmountFieldFromik
                      name='about.number_of_employees'
                      placeholder={translate('aboutEntreprise.numberOfEmployees')}
                      sx={{ maxWidth: isMobile ? '100%' : '225px' }}
                    />
                  </EditableTextField>
                </Stack>
                
                <EditableTextField
                  label={translate('aboutEntreprise.turnover')}
                  sx={{ width: isMobile ? '100%' : '22%' }}
                  value={getValue('about.revenue') || '_'}
                >
                  <AmountFieldFromik
                    name='about.revenue'
                    placeholder={translate('aboutEntreprise.turnover')}
                    sx={{ maxWidth: isMobile ? '100%' : '225px' }}
                  />
                </EditableTextField>
              </Stack>
            </SectionForm>
            <SectionFormAccordion
              title={translate('contactDetails.title')}
              subTitle={translate('contactDetails.mainAddress')}
            >
              <Stack spacing={isSmallMobile ? '20px' : isMobile ? '28px' : '34px'}>
                {/* Address Row 1 */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '24px'}>
                  <ReadOnlyField
                    label={t('common.civicNumber')}
                    value={getValue('address.civic_number')}
                    sx={{ 
                      width: isMobile ? '100%' : '85px',
                      mb: isMobile ? '0' : '0'
                    }}
                  />
                  <ReadOnlyField
                    label={t('common.street')}
                    value={getValue('address.street')}
                    sx={{ 
                      width: isMobile ? '100%' : '225px',
                      mb: isMobile ? '0' : '0'
                    }}
                  />
                  <ReadOnlyField 
                    label={t('common.office')} 
                    value={getValue('address.office')}
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                  />
                </Stack>
                
                {/* Address Row 2 */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '24px'}>
                  <ReadOnlyField
                    label={t('common.country')}
                    value={getValue('address.country')}
                    sx={{ 
                      width: isMobile ? '100%' : '334px',
                      mb: isMobile ? '0' : '0'
                    }}
                  />
                  <ReadOnlyField
                    label={t('common.province')}
                    value={getValue('address.province')}
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                  />
                </Stack>
                
                {/* Address Row 3 */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '24px'}>
                  <ReadOnlyField
                    label={t('common.city')}
                    value={getValue('address.city')}
                    sx={{ 
                      width: isMobile ? '100%' : '334px',
                      mb: isMobile ? '0' : '0'
                    }}
                  />
                  <ReadOnlyField
                    label={t('common.postalCode')}
                    value={getValue('address.postal_code')}
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                  />
                </Stack>
                
                <Typography 
                  variant='bodySmall'
                  sx={{
                    fontSize: isSmallMobile ? '12px' : isMobile ? '13px' : '14px',
                    lineHeight: isSmallMobile ? '16px' : isMobile ? '18px' : '20px'
                  }}
                >
                  {translate('contactDetails.phraseMainAddress')}
                </Typography>
              </Stack>
            </SectionFormAccordion>
            <SectionFormAccordion subTitle={translate('contactDetails.mailingAddress')}>
              <Stack spacing={isSmallMobile ? '20px' : isMobile ? '28px' : '34px'}>
                {/* Mailing Address Row 1 */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '24px'}>
                  <ReadOnlyField
                    label={t('common.civicNumber')}
                    value={getValue('correspondenceAddress.civic_number')}
                    sx={{ width: isMobile ? '100%' : '85px' }}
                  />
                  <ReadOnlyField
                    label={t('common.street')}
                    value={getValue('correspondenceAddress.street')}
                    sx={{ width: isMobile ? '100%' : '225px' }}
                  />
                  <ReadOnlyField
                    label={t('common.office')}
                    value={getValue('correspondenceAddress.office')}
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                  />
                </Stack>
                
                {/* Mailing Address Row 2 */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '24px'}>
                  <ReadOnlyField
                    label={t('common.country')}
                    value={getValue('correspondenceAddress.country')}
                    sx={{ width: isMobile ? '100%' : '334px' }}
                  />
                  <ReadOnlyField
                    label={t('common.province')}
                    value={getValue('correspondenceAddress.province')}
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                  />
                </Stack>
                
                {/* Mailing Address Row 3 */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '24px'}>
                  <ReadOnlyField
                    label={t('common.city')}
                    value={getValue('correspondenceAddress.city')}
                    sx={{ width: isMobile ? '100%' : '334px' }}
                  />
                  <ReadOnlyField
                    label={t('common.postalCode')}
                    value={getValue('correspondenceAddress.postal_code')}
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                  />
                </Stack>
              </Stack>
            </SectionFormAccordion>
            <SectionFormAccordion subTitle={translate('contactDetails.joinUs')}>
              {/* Contact Row 1 - Email and Website */}
              <Stack 
                direction={isMobile ? 'column' : 'row'} 
                spacing={isMobile ? '16px' : '24px'}
                sx={{ mb: isSmallMobile ? '16px' : isMobile ? '20px' : '27px' }}
              >
                <EditableTextField
                  label={t('common.email')}
                  sx={{ width: isMobile ? '100%' : '222px' }}
                  value={getValue('reach_us.email')}
                  required
                >
                  <EmailFieldFormik name='reach_us.email' />
                </EditableTextField>
                <EditableTextField
                  label={translate('contactDetails.website')}
                  sx={{ width: isMobile ? '100%' : '222px' }}
                  value={getValue('reach_us.website')}
                >
                  <OutlinedFieldFormik
                    name='reach_us.website'
                    placeholder='https://votresiteweb.ca'
                  />
                </EditableTextField>
              </Stack>
              
              {/* Contact Row 2 - Phone Numbers */}
              <Stack 
                direction={isMobile ? 'column' : 'row'} 
                spacing={isMobile ? '16px' : '24px'}
                sx={{ mb: isSmallMobile ? '16px' : isMobile ? '20px' : '27px' }}
              >
                <EditableTextField
                  label={t('common.phone')}
                  sx={{ width: isMobile ? '100%' : '204px' }}
                  value={getValue('reach_us.phone_number')}
                  required
                >
                  <PhoneFieldFromik name='reach_us.phone_number' />
                </EditableTextField>
                <EditableTextField
                  label={translate('contactDetails.tollFreePhone')}
                  sx={{ width: isMobile ? '100%' : '204px' }}
                  value={getValue('reach_us.toll_free_phone_number')}
                >
                  <PhoneFieldFromik
                    name='reach_us.toll_free_phone_number'
                    placeholder={'800 123-1234'}
                  />
                </EditableTextField>
                <EditableTextField
                  label={translate('contactDetails.faxMachine')}
                  sx={{ width: isMobile ? '100%' : '204px' }}
                  value={getValue('reach_us.fax')}
                >
                  <PhoneFieldFromik name='reach_us.fax' />
                </EditableTextField>
              </Stack>
              
              <Typography 
                variant='bodySmall'
                sx={{
                  fontSize: isSmallMobile ? '12px' : isMobile ? '13px' : '14px',
                  lineHeight: isSmallMobile ? '16px' : isMobile ? '18px' : '20px'
                }}
              >
                {translate('contactDetails.phraseJoinUs')}
              </Typography>
            </SectionFormAccordion>
            <SectionFormAccordion
              subTitle={translate('bondsAndGuatantees.RBQLicenseBond')}
              title={translate('bondsAndGuatantees.title')}
            >
              <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '24px'}>
                <ReadOnlyField
                  label={translate('bondsAndGuatantees.status')}
                  value={
                     getValue('rbq_license_deposit.status') === '1'
                      ? t('common.status.Actif')
                      : getValue('rbq_license_deposit.status') === '0'
                      ? t('common.status.Inactif')
                      : getValue('rbq_license_deposit.status')
                  }
                  sx={{ width: isMobile ? '100%' : '222px' }}
                />
                <ReadOnlyField
                  label={translate('bondsAndGuatantees.effectiveDate')}
                  value={getValue('rbq_license_deposit.effective_date')}
                  sx={{ width: isMobile ? '100%' : '222px' }}
                />
                <ReadOnlyField
                  label={t('common.noBond')}
                  value={getValue('rbq_license_deposit.no_deposit')}
                  sx={{ width: isMobile ? '100%' : '222px' }}
                />
                <ReadOnlyField
                  label={translate('bondsAndGuatantees.amount')}
                  value={
                    getValue('rbq_license_deposit.amount')
                      ? `${getValue('rbq_license_deposit.amount')} $`
                      : ''
                  }
                  sx={{ width: isMobile ? '100%' : 'auto' }}
                />
              </Stack>
            </SectionFormAccordion>
            <SectionFormAccordion subTitle={translate('bondsAndGuatantees.PGAGuaranteePlan')}>
              <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '24px'}>
                <ReadOnlyField
                  label={translate('bondsAndGuatantees.accreditationDate')}
                  value={getValue('pga_guarantee_plan.accreditation_date')}
                  sx={{ width: isMobile ? '100%' : '222px' }}
                />
                <ReadOnlyField
                  label={translate('bondsAndGuatantees.noResidentialBond')}
                  value={getValue('pga_guarantee_plan.residential_deposit_number')}
                  sx={{ width: isMobile ? '100%' : '225px' }}
                />
                <ReadOnlyField
                  label={translate('bondsAndGuatantees.PGAAccreditationNo')}
                  value={getValue('pga_guarantee_plan.no_accreditation')}
                  sx={{ width: isMobile ? '100%' : 'auto' }}
                />
              </Stack>
            </SectionFormAccordion>
            <SectionFormAccordion subTitle={translate('bondsAndGuatantees.GCRGuaranteePlan')}>
              <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '24px'}>
                <ReadOnlyField
                  label={translate('bondsAndGuatantees.dateOfIssueOfBond')}
                  value={getDayAndMonthNameYear(getValue('gcr_guarantee_plan.date_deposit_issue'))}
                  sx={{ width: isMobile ? '100%' : '222px' }}
                />
                <ReadOnlyField
                  label={t('common.noBond')}
                  value={getValue('gcr_guarantee_plan.no_deposit')}
                  sx={{ width: isMobile ? '100%' : 'auto' }}
                />
              </Stack>
            </SectionFormAccordion>
            <SectionFormAccordion subTitle={translate('bondsAndGuatantees.CNESST')}>
              <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '16px' : '24px'}>
                <ReadOnlyField
                  label={translate('bondsAndGuatantees.noClassification')}
                  value={getValue('cnesst.no_classification')}
                  sx={{ width: isMobile ? '100%' : '222px' }}
                />
                <ReadOnlyField
                  label={translate('bondsAndGuatantees.noEmployer')}
                  value={getValue('cnesst.no_employer')}
                  sx={{ width: isMobile ? '100%' : '225px' }}
                />
                <ReadOnlyField
                  label={translate('bondsAndGuatantees.contribution')}
                  value={getValue('cnesst.contribution')}
                  sx={{ width: isMobile ? '100%' : 'auto' }}
                />
              </Stack>
            </SectionFormAccordion>
          </Stack>
          {isGrantedToUpdate && <EditButtons sx={{ float: 'right' }} />}
        </Box>
      </FormikContext.Provider>
    </EditableContext.Provider>
  )
}

export default GeneralForm
